import { useEffect, useLayoutEffect, useRef } from 'react'
import isEqual from 'lodash/isEqual'
import Chart from '../chart'
import store from '../../../store'
import { selectOrdersHistory } from '../../../selectors/orders'
import { selectPositions } from '../../../selectors/positions'
import { selectTradeOrders } from '../../../selectors/strategyBuilder'
import { Order, OrderStatus, OrderType } from '../../../proto/generated/order'
import { Position } from '../../../proto/generated/position'

const SL_TP_STATUSES = [
    OrderStatus.OPEN,
    OrderStatus.PENDING,
    OrderStatus.TRIGGER_PENDING,
    OrderStatus.INACTIVE,
]
type GroupedOrder = {
    order: Order
    SL: Order | null
    TP: Order | null
}

type UseChartOrderDrawingSyncParams = {
    enabled?: boolean
    chartRef: React.MutableRefObject<Chart | undefined>
    chartContext: { chart: Chart } | null
    /** Post-render snapshot so SL/TP open orders are not read before useMemo updates. */
    openOrders: Order[]
    positions: Position[]
    buildChartPositions: (positions: Position[]) => Array<{
        position: Position & { isBasket?: boolean; realizedProfit?: number }
        stopLossOrder: Order | null
        targetOrder: Order | null
    }>
    buildOpenOrders: () => Order[]
}

export function useChartOrderDrawingSync({
    enabled = true,
    chartRef,
    chartContext,
    openOrders,
    positions,
    buildChartPositions,
    buildOpenOrders,
}: UseChartOrderDrawingSyncParams) {
    const buildChartPositionsRef = useRef(buildChartPositions)
    const buildOpenOrdersRef = useRef(buildOpenOrders)
    const lastSyncRef = useRef<{ chartPositions: unknown; openOrders: unknown } | null>(null)
    const syncNowRef = useRef<() => void>(() => {})
    const forceResyncRef = useRef(false)

    buildChartPositionsRef.current = buildChartPositions
    buildOpenOrdersRef.current = buildOpenOrders

    useEffect(() => {
        if (!enabled) return
        // Start unset so the first successful sync always runs. Initializing from store
        // would skip sync when positions loaded before chartContext was ready.
        let prevPositions: ReturnType<typeof selectPositions> | undefined
        let prevOrdersHistory: ReturnType<typeof selectOrdersHistory> | undefined
        let prevTradeOrders: ReturnType<typeof selectTradeOrders> | undefined
        lastSyncRef.current = null
        let rafId: number | null = null

        const syncNow = () => {
            const chart = chartRef.current
            if (!chartContext?.chart || !chart) return

            const state = store.getState()
            const storePositions = selectPositions(state)
            const ordersHistory = selectOrdersHistory(state)
            const tradeOrders = selectTradeOrders(state)
            if (
                !forceResyncRef.current &&
                storePositions === prevPositions &&
                ordersHistory === prevOrdersHistory &&
                tradeOrders === prevTradeOrders
            ) {
                return
            }
            forceResyncRef.current = false
            prevPositions = storePositions
            prevOrdersHistory = ordersHistory
            prevTradeOrders = tradeOrders
            const chartPositions = buildChartPositionsRef.current(storePositions)
            const openOrdersForChart = buildOpenOrdersRef.current()

            if (
                lastSyncRef.current &&
                isEqual(lastSyncRef.current.chartPositions, chartPositions) &&
                isEqual(lastSyncRef.current.openOrders, openOrdersForChart)
            ) {
                return
            }
            lastSyncRef.current = { chartPositions, openOrders: openOrdersForChart }

            chartPositions.forEach((p) => {
                const isBasket = (p.position as Position & { isBasket?: boolean })?.isBasket ?? false
                const stopLossOrder = isBasket
                    ? openOrdersForChart?.find(
                          (o) =>
                              o.orderType === OrderType.STOP_LOSS &&
                              SL_TP_STATUSES.includes(o.status) &&
                              o.basketId === p.position.basketId
                      )
                    : openOrdersForChart?.find(
                          (o) =>
                              o.orderType === OrderType.STOP_LOSS &&
                              SL_TP_STATUSES.includes(o.status) &&
                              o.basketId === p.position.basketId &&
                              o.product === p.position.product
                      )
                const targetOrder = isBasket
                    ? openOrdersForChart?.find(
                          (o) =>
                              o.orderType === OrderType.TARGET &&
                              SL_TP_STATUSES.includes(o.status) &&
                              o.basketId === p.position.basketId
                      )
                    : openOrdersForChart?.find(
                          (o) =>
                              o.orderType === OrderType.TARGET &&
                              SL_TP_STATUSES.includes(o.status) &&
                              o.basketId === p.position.basketId &&
                              o.product === p.position.product
                      )

                p.stopLossOrder = stopLossOrder ?? null
                p.targetOrder = targetOrder ?? null
            })

            const groupedOrders: GroupedOrder[] = []
            const batchMap = new Map<string | undefined, Order[]>()

            for (const order of openOrdersForChart) {
                const key = order.batchId ?? undefined
                if (!batchMap.has(key)) {
                    batchMap.set(key, [])
                }
                batchMap.get(key)!.push(order)
            }
            batchMap.forEach((orders) => {
                const mainOrder = orders.find(
                    (o) => o.orderType !== OrderType.STOP_LOSS && o.orderType !== OrderType.TARGET
                )
                const slOrder = orders.find((o) => o.orderType === OrderType.STOP_LOSS) ?? null
                const tpOrder = orders.find((o) => o.orderType === OrderType.TARGET) ?? null
                if (mainOrder) {
                    groupedOrders.push({
                        order: mainOrder,
                        SL: slOrder,
                        TP: tpOrder,
                    })
                }
            })

            chart.updateOrdersOnChartDrawing(chartPositions, groupedOrders)
        }

        syncNowRef.current = syncNow

        const scheduleSync = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId)
            }
            rafId = requestAnimationFrame(() => {
                rafId = null
                syncNow()
            })
        }

        scheduleSync()
        const unsubscribe = store.subscribe(scheduleSync)
        return () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId)
            }
            unsubscribe()
        }
    }, [enabled, chartContext?.chart, chartRef, buildChartPositions, buildOpenOrders])

    useLayoutEffect(() => {
        if (!enabled) return
        forceResyncRef.current = true
        lastSyncRef.current = null
        syncNowRef.current()
    }, [enabled, openOrders, positions, chartContext?.chart, buildChartPositions])
}
